/**
 * AddPersonDialog Component
 *
 * A dialog component for adding or editing personnel information in the system.
 * This component handles:
 * - Creating new personnel records
 * - Editing existing personnel information
 * - Form validation based on database schema constraints
 * - Error handling and user feedback
 *
 * The form collects:
 * - First Name (required, max 50 chars)
 * - Last Name (optional, max 50 chars)
 * - Email (optional, max 50 chars)
 * - Organization (required, max 50 chars)
 */

import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  Alert,
} from "@mui/material";
import { postFormData } from "../../../services/api";

const AddPersonDialog = ({ open, onClose, onSuccess, initialData = null }) => {
  // Initialize form state with either empty values or existing data for editing
  const [personInfo, setPersonInfo] = useState({
    firstname: initialData?.firstname || "",
    lastname: initialData?.lastname || "",
    email: initialData?.email || "",
    organization: initialData?.organization || "",
  });

  // State for tracking form validation errors
  const [errors, setErrors] = useState({});

  /**
   * Validates the form inputs based on database schema constraints
   * Checks for:
   * - Required fields
   * - Maximum length constraints
   * - Email format (currently commented out)
   *
   * @returns {boolean} True if form is valid, false otherwise
   */
  const validateForm = () => {
    const newErrors = {};

    // Validate based on CHARACTER VARYING lengths from schema
    if (!personInfo.firstname) {
      newErrors.firstname = "First name is required";
    } else if (personInfo.firstname.length > 50) {
      newErrors.firstname = "First name must be 50 characters or less";
    }

    // Last name validation (optional field)
    if (personInfo.lastname.length > 50) {
      newErrors.lastname = "Last name must be 50 characters or less";
    }

    // Email validation (optional field)
    if (personInfo.email.length > 50) {
      newErrors.email = "Email must be 50 characters or less";
    }
    // Email format validation (currently disabled)
    // if (!/\S+@\S+\.\S+/.test(personInfo.email)) {
    // 	newErrors.email = "Invalid email format";
    // }

    // Organization validation (required field)
    if (!personInfo.organization) {
      newErrors.organization = "Organization is required";
    }
    if (personInfo.organization.length > 50) {
      newErrors.organization = "Organization must be 50 characters or less";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handles form submission
   * - Validates form data
   * - Posts data to the API
   * - Handles success and error cases
   */
  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      await postFormData("metadata/person", personInfo);
      onSuccess();
      handleClose();
    } catch (error) {
      setErrors({ submit: error.message || "Failed to add person" });
    }
  };

  /**
   * Handles dialog closing
   * - Resets form state
   * - Clears errors
   * - Calls parent's onClose handler
   */
  const handleClose = () => {
    setPersonInfo({
      firstname: "",
      lastname: "",
      email: "",
      organization: "",
    });
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {initialData ? "Edit Person" : "Add New Person"}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {/* Display submission errors if any */}
          {errors.submit && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.submit}
            </Alert>
          )}

          <Grid container spacing={2}>
            {/* First Name Field */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="First Name"
                value={personInfo.firstname}
                onChange={(e) =>
                  setPersonInfo((prev) => ({
                    ...prev,
                    firstname: e.target.value,
                  }))
                }
                error={!!errors.firstname}
                helperText={errors.firstname}
                required
                inputProps={{ maxLength: 50 }}
              />
            </Grid>

            {/* Last Name Field */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={personInfo.lastname}
                onChange={(e) =>
                  setPersonInfo((prev) => ({
                    ...prev,
                    lastname: e.target.value,
                  }))
                }
                error={!!errors.lastname}
                helperText={errors.lastname}
                inputProps={{ maxLength: 50 }}
              />
            </Grid>

            {/* Email Field */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={personInfo.email}
                onChange={(e) =>
                  setPersonInfo((prev) => ({
                    ...prev,
                    email: e.target.value,
                  }))
                }
                error={!!errors.email}
                helperText={errors.email}
                inputProps={{ maxLength: 50 }}
              />
            </Grid>

            {/* Organization Field */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Organization"
                value={personInfo.organization}
                onChange={(e) =>
                  setPersonInfo((prev) => ({
                    ...prev,
                    organization: e.target.value,
                  }))
                }
                required
                error={!!errors.organization}
                helperText={errors.organization}
                inputProps={{ maxLength: 50 }}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      {/* Dialog Action Buttons */}
      <DialogActions sx={{ p: 2.5, pt: 1.5 }}>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={Object.keys(errors).length > 0}
        >
          {initialData ? "Save Changes" : "Add Person"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPersonDialog;
